import { debounce } from 'lodash-es';
import { type UIEvent, useCallback, useState } from 'react';

const DEFAULT_SCROLL_SPY_DEBOUNCE_MILLISECONDS = 100 as const;

export interface ScrollSpy {
  isScrollable: boolean;
  scrollLimitY: number;
  scrollLimitX: number;
  scrollY: number;
  scrollX: number;
  isInitialized: boolean;
}

export interface UseScrollSpyResult {
  scrollSpy: ScrollSpy;
  onScroll: (event: UIEvent<HTMLElement>) => void;
}

export interface UseScrollSpyOptions {
  debounceMilliseconds?: number;
}

export const useScrollSpy = (
  { debounceMilliseconds = DEFAULT_SCROLL_SPY_DEBOUNCE_MILLISECONDS }: UseScrollSpyOptions = {
    debounceMilliseconds: DEFAULT_SCROLL_SPY_DEBOUNCE_MILLISECONDS,
  },
): UseScrollSpyResult => {
  const [scrollSpy, setScrollSpy] = useState<UseScrollSpyResult['scrollSpy']>({
    isScrollable: false,
    scrollLimitY: 99999,
    scrollLimitX: 99999,
    scrollY: 0,
    scrollX: 0,
    isInitialized: false,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  const onScroll: UseScrollSpyResult['onScroll'] = useCallback(
    debounce(
      (event) => {
        if (event.currentTarget) {
          setScrollSpy({
            isScrollable: event.currentTarget.scrollHeight > event.currentTarget.clientHeight,
            scrollLimitY:
              event.currentTarget.scrollHeight -
              event.currentTarget.clientHeight -
              /* buffer for padding & animation */ 5,
            scrollLimitX:
              event.currentTarget.scrollWidth -
              event.currentTarget.clientWidth -
              /* buffer for padding & animation */ 5,
            scrollX: event.currentTarget.scrollLeft,
            scrollY: event.currentTarget.scrollTop,
            isInitialized: true,
          });
        }
      },
      debounceMilliseconds,
      { leading: true, trailing: true },
    ),
    [scrollSpy],
  );

  return { scrollSpy, onScroll };
};
